/**
 * Types partagés frontend SIM GROUP Holding Platform.
 * Miroir des DTOs côté backend NestJS — à garder synchronisé manuellement
 * tant qu'on n'a pas mis en place une génération automatique depuis openapi.yaml.
 */

export type Role =
  | 'admin_holding'
  | 'auditeur'
  | 'dg_filiale'
  | 'daf'
  | 'manager'
  | 'rh'
  | 'acheteur'
  | 'commercial'
  | 'chef_equipe'
  // Le back-end l'accepte depuis le portail terrain (gardes de
  // filiale.controller.ts) ; il manquait ici, donc aucune entrée de menu ne
  // pouvait lui être ouverte.
  | 'responsable_production';

export type Metier =
  | 'beton'
  | 'transport_minier'
  | 'telecom_wifi'
  | 'impression'
  | 'immobilier'
  | 'generic';

export type Currency = 'FCFA' | 'GNF' | 'EUR' | 'USD';

export interface User {
  id:          string;
  email:       string;
  fullName:    string;
  role:        Role;
  filialeCode: string;
  filialeName: string;
}

export interface Filiale {
  id:             string;
  filialeKey:     string;
  name:           string;
  fullName:       string;
  pays:           string;
  devise:         Currency;
  logoPath:       string;
  colorPrimary:   string;
  colorSecondary: string;
  colorAccent:    string;
  isActive:       boolean;
  metier:         Metier;
  metierLabel:    string;
  metierConfig:   Record<string, unknown>;
}

export interface LoginResponse {
  accessToken?:    string;
  refreshToken?:   string;
  expiresIn?:      number;
  user?:           User;
  requires2FA?:    boolean;
  challengeToken?: string;
}

export interface ApiSuccess<T> {
  success: true;
  data: T;
}

export interface ApiError {
  success: false;
  error:   { code: string; message: string };
  timestamp: string;
  path:    string;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

// ── CATALOGUE PRODUITS DYNAMIQUE (module operations/beton) ──────────────────

export type FamilleProduit =
  | 'POTEAUX_BA'
  | 'AGGLOS'
  | 'VOIRIE_VRD'
  | 'BETON_PRET_EMPLOI'
  | 'AUTRE';

export type MachineCode =
  | 'DONGYUE_QT18'
  | 'HAIYU_COFFRAGE'
  | 'COFFRAGE_MANUEL'
  | 'TOUPIE_BPE'
  | 'AUTRE';

export interface MatierePremiere {
  id:                 string;
  filialeId:          string;
  code:               string;
  nom:                string;
  description:        string | null;
  unite:              string;
  prixUnitaireCents:  string;  // BigInt sérialisé
  fournisseurNom:     string | null;
  stockActuel:        string;  // Decimal sérialisé
  seuilAlerte:        string;
  actif:              boolean;
  createdAt:          string;
  updatedAt:          string;
  // Sprint 3 — Module 7 (Stocks enrichi)
  type:               'ciment' | 'granulat' | 'eau' | 'adjuvant' | 'acier_ha' | 'acier_treillis' | 'accessoire' | 'autre' | null;
  diametreMm:         number | null;
  classeAcier:        string | null;
  classeCiment:       string | null;
  granulometrie:      string | null;
  cmpCents:           string | null;
  consoMoyenneJ:      string | null;
}

export interface ProduitBOMItem {
  id:                string;
  produitId:         string;
  matierePremiereId: string;
  quantite:          string;  // Decimal sérialisé
  notes:             string | null;
  matierePremiere:   MatierePremiere;
  createdAt:         string;
  updatedAt:         string;
}

/** Groupe de catalogue (24/10/2026) : rangement propre à chaque société, indépendant de la famille. */
export interface CatalogueGroupe {
  id:      string;
  libelle: string;
  ordre:   number;
  actif:   boolean;
}

export interface Produit {
  id:                 string;
  filialeId:          string;
  code:               string;
  famille:            FamilleProduit;
  sousFamille:        string | null;
  groupeId:           string | null;
  groupe?:            CatalogueGroupe | null;
  designation:        string;
  longueurM:          string | null;
  largeurMm:          number | null;
  hauteurMm:          number | null;
  epaisseurMm:        number | null;
  poidsKg:            string | null;
  volumeBetonM3:      string | null;
  resistanceFc28:     string | null;
  norme:              string | null;
  classeProduit:      string | null;
  classeBeton:        ClasseBeton | null;
  effortNominalDaN:   number | null;
  chargeRuptureDaN:   number | null;
  // Préfa Phase 1
  formuleBetonId:        string | null;
  ferraillageKgParPiece: string | null;
  delaiCureJours:        number | null;
  mouleIdDefault:        string | null;
  photoUrl:              string | null;
  ficheTechniquePdfUrl:  string | null;
  // Commerce
  prixVenteCents:     string;
  uniteVente:         string;
  machineFabrication: MachineCode | null;
  tempsCycleMin:      string | null;
  /**
   * Cumul de production, PAS une disponibilité : incrémenté à la clôture d'un
   * OF et jamais décrémenté à la sortie. Ne jamais l'afficher comme un stock —
   * utiliser `disponibleParc`.
   */
  stockActuel:        string;
  seuilAlerte:        string;
  /** Entré au parc et pas sorti — le stock réellement vendable. */
  disponibleParc:     number;
  /** Part du disponible déjà réservée à une commande. */
  reserveParc:        number;
  /** Cumul de ce qui est sorti du parc (expédié). */
  sortiParc:          number;
  actif:              boolean;
  createdAt:          string;
  updatedAt:          string;
}

// ── LeadBeton Préfabriqué Phase 1 + Sprint 2 ───────────────────────────────

export type StatutGachee = 'brouillon' | 'valide' | 'hors_norme';
export const STATUT_GACHEE_LABELS: Record<StatutGachee, string> = {
  brouillon: 'Brouillon', valide: 'Validée', hors_norme: 'Hors norme',
};
export const STATUT_GACHEE_COLORS: Record<StatutGachee, string> = {
  brouillon: 'var(--tx-3)', valide: 'var(--success)', hors_norme: 'var(--warning)',
};

export type StatutEprouvette = 'moulee' | 'en_attente_r7' | 'en_attente_r28' | 'conforme' | 'non_conforme' | 'ecartee';
export const STATUT_EPROUVETTE_LABELS: Record<StatutEprouvette, string> = {
  moulee:         'Moulée',
  en_attente_r7:  'Attente R7',
  en_attente_r28: 'Attente R28',
  conforme:       'Conforme',
  non_conforme:   'Non conforme',
  ecartee:        'Écartée',
};

export type TypeMouvementParc = 'entree' | 'transfert' | 'reservation' | 'liberation' | 'sortie';
export const TYPE_MVT_PARC_LABELS: Record<TypeMouvementParc, string> = {
  entree:      'Entrée parc',
  transfert:   'Transfert zone',
  reservation: 'Réservation',
  liberation:  'Libération',
  sortie:      'Sortie parc',
};

export interface Gachee {
  id:                 string;
  filialeId:          string;
  reference:          string;
  ordreId:            string;
  formuleBetonId:     string | null;
  operateurUserId:    string | null;
  centrale:           string | null;
  heureCoulage:       string;
  volumeM3:           string;
  slumpMm:            number | null;
  slumpCibleMm:       number | null;
  tempEauC:           string | null;
  tempAmbianteC:      string | null;
  rapportEC:          string | null;
  statut:             StatutGachee;
  notes:              string | null;
  createdAt:          string;
  updatedAt:          string;
  ordre?:        { id: string; reference: string; produitId?: string };
  formuleBeton?: { id: string; code: string; libelle: string; classeBeton?: string; fc28TargetMpa?: string | null } | null;
  lots?:         Array<{ id: string; lotNumber: string; quantite?: string; statut?: string }>;
  eprouvettes?:  Array<{ id: string; reference: string; statut: StatutEprouvette }>;
}

export interface Eprouvette {
  id:              string;
  filialeId:       string;
  reference:       string;
  gacheeId:        string;
  typeEprouvette:  string;
  dateMoulage:     string;
  dateR7Prevue:    string;
  dateR7Reelle:    string | null;
  fc7Mesure:       string | null;
  dateR28Prevue:   string;
  dateR28Reelle:   string | null;
  fc28Mesure:      string | null;
  fc28CibleMpa:    string | null;
  statut:          StatutEprouvette;
  notes:           string | null;
  createdAt:       string;
  updatedAt:       string;
  gachee?: {
    id: string; reference: string; ordreId: string; heureCoulage: string;
    ordre?: { id: string; reference: string };
    formuleBeton?: { id: string; code: string; libelle: string; fc28TargetMpa?: string | null } | null;
  };
}

export interface MouvementParc {
  id:          string;
  filialeId:   string;
  lotId:       string;
  type:        TypeMouvementParc;
  zoneFrom:    string | null;
  zoneTo:      string | null;
  motif:       string | null;
  userId:      string | null;
  commandeId:  string | null;
  createdAt:   string;
  lot?: { id: string; lotNumber: string; ordreId: string };
}

export interface DashboardQC {
  periode90j: {
    total:          number;
    conformes:      number;
    nonConformes:   number;
    ecartees:       number;
    tauxConformite: number | null;
  };
  attenteR28: number;
  courbeR28:  Array<{ date: string | undefined; fc28: number | null; cible: number | null }>;
}

export interface InventaireParc {
  total: { lots: number; quantite: number; reserves: number };
  zones: Array<{ zone: string; nbLots: number; quantite: number }>;
}

// ── LeadBeton Sprint 6 — Dashboard DG · CRM Pipeline · AffectationOf ───────

export type PipelineStageCrm = 'prospect' | 'devis_envoye' | 'etude_technique' | 'negociation' | 'gagne' | 'perdu';
export const PIPELINE_STAGE_LABELS: Record<PipelineStageCrm, string> = {
  prospect:         'Prospect',
  devis_envoye:     'Devis envoyé',
  etude_technique:  'Étude technique',
  negociation:      'Négociation',
  gagne:            'Gagné',
  perdu:            'Perdu',
};
export const PIPELINE_STAGE_COLORS: Record<PipelineStageCrm, string> = {
  prospect:         'var(--tx-3)',
  devis_envoye:     'var(--info)',
  etude_technique:  'var(--warning)',
  negociation:      'var(--warning)',
  gagne:            'var(--success)',
  perdu:            'var(--danger)',
};

export type RoleAffectationOf = 'ferrailleur' | 'coffreur' | 'operateur_beton' | 'cariste' | 'controleur_qc' | 'chef_equipe' | 'autre';
export const ROLE_AFFECTATION_LABELS: Record<RoleAffectationOf, string> = {
  ferrailleur:     'Ferrailleur',
  coffreur:        'Coffreur',
  operateur_beton: 'Opérateur béton',
  cariste:         'Cariste',
  controleur_qc:   'Contrôleur QC',
  chef_equipe:     'Chef d\'équipe',
  autre:           'Autre',
};

export interface AffectationOf {
  id:               string;
  filialeId:        string;
  ordreId:          string;
  userId:           string;
  role:             RoleAffectationOf;
  dateAffectation:  string;
  heuresEffectuees: string | null;
  notes:            string | null;
  createdAt:        string;
  updatedAt:        string;
  ordre?: { id: string; reference: string; produit?: { code: string; designation: string } };
  user?:  { id: string; fullName: string; email: string; role: string };
}

export interface DashboardDg {
  periode: { moisCourant: string; jourReference: string };
  production: {
    ofPlanifies:   number;
    ofEnCours:     number;
    ofTermines:    number;
    ofRetardes:    number;
    lotsValideMois:    number;
    lotsTotalMois:     number;
    tauxConformiteLots: number | null;
    delaiMoyenOfLivraisonJours: number | null;
    moules: {
      total:         number;
      operationnels: number;
      maintenance:   number;
      hors_service:  number;
      a_entretenir:  number;
    };
  };
  qualite: {
    eprouvettes90j: { total: number; conformes: number; nonConformes: number; ecartees: number; enAttenteR28: number };
    tauxConformite: number | null;
    objectif:       number;
    rebut90j:       number;
    dncOuvertes:    number;
    courbeR28: Array<{ date?: string; fc28: number | null; cible: number | null }>;
  };
  stocks: {
    total:                  number;
    actives:                number;
    alertes:                number;
    critiques: Array<{
      id: string; code: string; nom: string; type: string | null; unite: string;
      stock: number; seuil: number; conso30j: number; consoJour: number;
      joursRestants: number | null; valorisationCents: number | null; sousSeuil: boolean;
    }>;
    valorisationTotalCents: number;
  };
  finance: {
    caMoisCents:       number;
    caMoisPrecCents:   number;
    evolCaPct:         number | null;
    facturesEmises:    number;
    facturesPayees:    number;
    facturesEnRetard:  number;
    encoursClient: {
      totalCents: number;
      ageBuckets: { '0_30': number; '30_60': number; '60_90': number; '90_plus': number };
    };
  };
  alertes: {
    total: number;
    parCategorie: { production: number; qualite: number; stocks: number; finance: number };
  };
}

// ── LeadBeton Sprint 5 — Achats & Maintenance moules ───────────────────────

export type CategorieFournisseur = 'cimenterie' | 'carriere' | 'siderurgiste' | 'adjuvants' | 'consommables' | 'transport' | 'autre';
export const CATEGORIE_FOURNISSEUR_LABELS: Record<CategorieFournisseur, string> = {
  cimenterie:   'Cimenterie',
  carriere:     'Carrière',
  siderurgiste: 'Sidérurgiste',
  adjuvants:    'Adjuvants',
  consommables: 'Consommables',
  transport:    'Transport',
  autre:        'Autre',
};

export type StatutBonCommande = 'brouillon' | 'emis' | 'reception_partielle' | 'recu_complet' | 'annule';
export const STATUT_BCA_LABELS: Record<StatutBonCommande, string> = {
  brouillon:           'Brouillon',
  emis:                'Émis',
  reception_partielle: 'Réception partielle',
  recu_complet:        'Reçu complet',
  annule:              'Annulé',
};
export const STATUT_BCA_COLORS: Record<StatutBonCommande, string> = {
  brouillon:           'var(--tx-3)',
  emis:                'var(--info)',
  reception_partielle: 'var(--warning)',
  recu_complet:        'var(--success)',
  annule:              'var(--danger)',
};

export type TypeMaintenanceMoule = 'nettoyage' | 'huilage' | 'controle_geometrie' | 'reparation' | 'remplacement_piece' | 'autre';
export const TYPE_MAINTENANCE_MOULE_LABELS: Record<TypeMaintenanceMoule, string> = {
  nettoyage:           'Nettoyage',
  huilage:             'Huilage',
  controle_geometrie:  'Contrôle géométrie',
  reparation:          'Réparation',
  remplacement_piece:  'Remplacement pièce',
  autre:               'Autre',
};

export type StatutMaintenanceMoule = 'planifiee' | 'en_cours' | 'terminee' | 'annulee';
export const STATUT_MAINTENANCE_MOULE_LABELS: Record<StatutMaintenanceMoule, string> = {
  planifiee: 'Planifiée',
  en_cours:  'En cours',
  terminee:  'Terminée',
  annulee:   'Annulée',
};

export interface Fournisseur {
  id:                 string;
  filialeId:          string;
  code:               string;
  raisonSociale:      string;
  categorie:          CategorieFournisseur;
  contactNom:         string | null;
  email:              string | null;
  tel:                string | null;
  adresse:            string | null;
  ville:              string | null;
  pays:               string | null;
  nif:                string | null;
  iban:               string | null;
  banque:             string | null;
  delaiPaiementJours: number;
  notes:              string | null;
  actif:              boolean;
  createdAt:          string;
  updatedAt:          string;
}

export interface LigneBca {
  id:                 string;
  bonCommandeId:      string;
  matierePremiereId:  string;
  quantite:           string;
  quantiteRecue:      string;
  prixUnitaireCents:  string;
  totalLineCents:     string;
  notes:              string | null;
  createdAt:          string;
  updatedAt:          string;
  matierePremiere?:   { id: string; code: string; nom: string; unite: string; type?: string | null };
}

export interface BonCommandeAchat {
  id:                  string;
  filialeId:           string;
  reference:           string;
  fournisseurId:       string;
  dateCommande:        string;
  dateLivraisonPrevue: string | null;
  statut:              StatutBonCommande;
  montantHtCents:      string;
  tvaPct:              string;
  notes:               string | null;
  declencheurType:     string | null;
  createdById:         string;
  createdAt:           string;
  updatedAt:           string;
  fournisseur?: { id: string; code: string; raisonSociale: string; categorie?: CategorieFournisseur };
  lignes?:      LigneBca[];
  receptions?:  Array<{ id: string; reference: string; dateReception: string; refBLFournisseur: string | null }>;
  _count?:      { lignes: number; receptions: number };
}

export interface LigneReception {
  id:                 string;
  receptionId:        string;
  ligneBcaId:         string;
  quantiteRecue:      string;
  prixUnitaireCents:  string;
  notes:              string | null;
  createdAt:          string;
  ligneBca?: LigneBca;
}

export interface ReceptionAchat {
  id:                string;
  filialeId:         string;
  reference:         string;
  bonCommandeId:     string;
  dateReception:     string;
  refBLFournisseur:  string | null;
  notes:             string | null;
  createdById:       string;
  createdAt:         string;
  updatedAt:         string;
  bonCommande?: { id: string; reference: string; fournisseur?: { id: string; raisonSociale: string } };
  lignes?:      LigneReception[];
  _count?:      { lignes: number };
}

export interface MaintenanceMoule {
  id:              string;
  filialeId:       string;
  reference:       string;
  mouleId:         string;
  type:            TypeMaintenanceMoule;
  statut:          StatutMaintenanceMoule;
  datePlanifiee:   string;
  dateDebut:       string | null;
  dateFin:         string | null;
  cyclesAuDebut:   number | null;
  description:     string;
  technicienNom:   string | null;
  coutCents:       string | null;
  resultat:        string | null;
  createdById:     string;
  createdAt:       string;
  updatedAt:       string;
  moule?: { id: string; code: string; libelle: string; statut?: string; nbCyclesEffectues?: number; prochainEntretienCycles?: number | null };
}

// ── LeadBeton Sprint 4 — Chantiers enrichi & Factures auto ─────────────────

export type StatutReclamation = 'ouverte' | 'en_traitement' | 'resolue' | 'cloturee';
export const STATUT_RECLAMATION_LABELS: Record<StatutReclamation, string> = {
  ouverte:       'Ouverte',
  en_traitement: 'En traitement',
  resolue:       'Résolue',
  cloturee:      'Clôturée',
};

export interface ReclamationChantier {
  id:            string;
  filialeId:     string;
  chantierId:    string;
  reference:     string;
  objet:         string;
  description:   string | null;
  gravite:       string | null;
  statut:        StatutReclamation;
  ouverteParId:  string | null;
  affecteId:     string | null;
  dateOuverture: string;
  dateCloture:   string | null;
  resolution:    string | null;
  notes:         string | null;
  createdAt:     string;
  updatedAt:     string;
  chantier?: { id: string; code: string; nom: string; clientId?: string };
}

// ── LeadBeton Sprint 3 — Stocks enrichi & Expéditions ──────────────────────

export type TypeMatierePremiere = 'ciment' | 'granulat' | 'eau' | 'adjuvant' | 'acier_ha' | 'acier_treillis' | 'accessoire' | 'autre';
export const TYPE_MP_LABELS: Record<TypeMatierePremiere, string> = {
  ciment:         'Ciment',
  granulat:       'Granulats',
  eau:            'Eau',
  adjuvant:       'Adjuvant',
  acier_ha:       'Acier HA',
  acier_treillis: 'Treillis soudé',
  accessoire:     'Accessoires',
  autre:          'Autre',
};

export type TypeMouvementStock = 'entree_achat' | 'sortie_gachee' | 'sortie_of' | 'sortie_manuelle' | 'inventaire';
export const TYPE_MVT_STOCK_LABELS: Record<TypeMouvementStock, string> = {
  entree_achat:    'Entrée achat',
  sortie_gachee:   'Sortie gâchée',
  sortie_of:       'Sortie OF',
  sortie_manuelle: 'Sortie manuelle',
  inventaire:      'Inventaire',
};

export interface MouvementStock {
  id:                  string;
  filialeId:           string;
  matierePremiereId:   string;
  type:                TypeMouvementStock;
  quantite:            string;
  prixUnitaireCents:   string | null;
  stockApres:          string | null;
  gacheeId:            string | null;
  ordreFabricationId:  string | null;
  refExterne:          string | null;
  userId:              string | null;
  motif:               string | null;
  createdAt:           string;
  matierePremiere?: { id: string; code: string; nom: string; unite: string; type: TypeMatierePremiere | null };
}

export interface StockHealthRow {
  id:                  string;
  code:                string;
  nom:                 string;
  type:                TypeMatierePremiere | null;
  unite:               string;
  stock:               number;
  seuil:               number;
  conso30j:            number;
  consoMoyenneJ:       number;
  joursStockRestants:  number | null;
  sousSeuil:           boolean;
  valorisationCents:   number | null;
}

export interface StockHealth {
  total:   { lignes: number; alertes: number };
  lignes:  StockHealthRow[];
  alertes: StockHealthRow[];
}

export type StatutExpedition = 'brouillon' | 'preparee' | 'expediee' | 'livree' | 'refusee' | 'annulee';
export const STATUT_EXPEDITION_LABELS: Record<StatutExpedition, string> = {
  brouillon: 'Brouillon',
  preparee:  'Préparée',
  expediee:  'Expédiée',
  livree:    'Livrée',
  refusee:   'Refusée',
  annulee:   'Annulée',
};
export const STATUT_EXPEDITION_COLORS: Record<StatutExpedition, string> = {
  brouillon: 'var(--tx-3)',
  preparee:  'var(--info)',
  expediee:  'var(--warning)',
  livree:    'var(--success)',
  refusee:   'var(--danger)',
  annulee:   'var(--tx-3)',
};

export type TypeTransportExpedition = 'plateau' | 'grue' | 'toupie' | 'sous_traitant' | 'autre';
export const TYPE_TRANSPORT_LABELS: Record<TypeTransportExpedition, string> = {
  plateau:       'Plateau',
  grue:          'Grue',
  toupie:        'Toupie',
  sous_traitant: 'Sous-traitant',
  autre:         'Autre',
};

export interface LigneExpedition {
  id:           string;
  expeditionId: string;
  lotId:        string;
  quantite:     string;
  poidsKg:      string | null;
  notes:        string | null;
  createdAt:    string;
  lot?: {
    id: string; lotNumber: string; quantite: string; qrCode: string;
    ordre?: {
      id: string; reference: string;
      produit: { id: string; code: string; designation: string; famille: FamilleProduit; uniteVente: string; poidsKg: string | null };
    };
  };
}

export interface Expedition {
  id:                  string;
  filialeId:           string;
  reference:           string;
  commandeId:          string | null;
  chantierId:          string | null;
  adresseLivraison:    string | null;
  typeTransport:       TypeTransportExpedition;
  immatriculations:    string | null;
  chauffeur:           string | null;
  telChauffeur:        string | null;
  sousTraitant:        string | null;
  statut:              StatutExpedition;
  dateDepart:          string | null;
  dateLivraison:       string | null;
  poidsTotalKg:        string;
  nbCamionsEstime:     number;
  capaciteCamionKg:    string;
  signatureClientHash: string | null;
  signatureClientNom:  string | null;
  signatureClientAt:   string | null;
  raisonRefus:         string | null;
  /** ANNULATION : qui, quand, pourquoi (statut annulee). */
  annuleLe?:           string | null;
  annuleParNom?:       string | null;
  annuleMotif?:        string | null;
  notes:               string | null;
  /** BL-TERRAIN : 'terrain' = émis et signé sur le portail par une personne désignée ; à viser par le DAF. */
  source?:                  'bureau' | 'terrain';
  emisParNom?:              string | null;
  signatureChauffeurNom?:   string | null;
  signatureChauffeurAt?:    string | null;
  photoBon?:                string | null;
  viseAt?:                  string | null;
  viseParNom?:              string | null;
  viseNote?:                string | null;
  /** BL-LIEN : 'sur_place' (signé sur le téléphone du chef) ou 'lien' (signé par le client sur son téléphone). */
  signatureClientMode?:     'sur_place' | 'lien' | null;
  signatureClientObservations?: string | null;
  pdfPath?:                 string | null;
  createdById:         string;
  createdAt:           string;
  updatedAt:           string;
  commande?: { id: string; reference: string; clientId?: string; statut?: StatutCommande; montantHtFcfa?: string } | null;
  chantier?: { id: string; code: string; nom: string; adresse?: string | null } | null;
  lignes?:   LigneExpedition[];
  _count?:   { lignes: number };
}

export type ClasseBeton = 'c16_20' | 'c20_25' | 'c25_30' | 'c30_37' | 'c35_45' | 'c40_50';
export const CLASSE_BETON_LABELS: Record<ClasseBeton, string> = {
  c16_20: 'C16/20', c20_25: 'C20/25', c25_30: 'C25/30',
  c30_37: 'C30/37', c35_45: 'C35/45', c40_50: 'C40/50',
};

export type StatutMoule = 'operationnel' | 'maintenance' | 'hors_service';
export const STATUT_MOULE_LABELS: Record<StatutMoule, string> = {
  operationnel: 'Opérationnel', maintenance: 'En maintenance', hors_service: 'Hors service',
};
export const STATUT_MOULE_COLORS: Record<StatutMoule, string> = {
  operationnel: '#16a34a', maintenance: '#f59e0b', hors_service: '#dc2626',
};

export type StatutCommande =
  | 'devis' | 'confirmee' | 'en_fabrication' | 'prete_au_parc'
  | 'expediee' | 'livree' | 'facturee' | 'annulee';
export const STATUT_COMMANDE_LABELS: Record<StatutCommande, string> = {
  devis: 'Devis', confirmee: 'Confirmée', en_fabrication: 'En fabrication',
  prete_au_parc: 'Prête au parc', expediee: 'Expédiée', livree: 'Livrée',
  facturee: 'Facturée', annulee: 'Annulée',
};
export const STATUT_COMMANDE_COLORS: Record<StatutCommande, string> = {
  devis: '#94a3b8', confirmee: '#3b82f6', en_fabrication: '#f59e0b',
  prete_au_parc: '#a855f7', expediee: '#06b6d4', livree: '#10b981',
  facturee: '#16a34a', annulee: '#cbd5e1',
};

export interface FormuleBeton {
  id:                string;
  filialeId:         string;
  code:              string;
  libelle:           string;
  classeBeton:       ClasseBeton;
  cimentKgM3:        string | null;
  sableKgM3:         string | null;
  gravier5_15KgM3:   string | null;
  gravier15_25KgM3:  string | null;
  eauLitresM3:       string | null;
  adjuvantKgM3:      string | null;
  fc28TargetMpa:     string | null;
  rapportEC:         string | null;
  notes:             string | null;
  actif:             boolean;
  createdAt:         string;
  updatedAt:         string;
}

export interface Moule {
  id:                       string;
  filialeId:                string;
  code:                     string;
  libelle:                  string;
  longueurMm:               number | null;
  largeurMm:                number | null;
  nbEmpreintes:             number;
  dateMiseEnService:        string | null;
  nbCyclesEffectues:        number;
  prochainEntretienCycles:  number | null;
  statut:                   StatutMoule;
  notes:                    string | null;
  createdAt:                string;
  updatedAt:                string;
}

export interface EquipeProduction {
  id:         string;
  filialeId:  string;
  code:       string;
  nom:        string;
  chefNom:    string | null;
  specialite: string | null;
  actif:      boolean;
  notes:      string | null;
  createdAt:  string;
  updatedAt:  string;
}

export interface LigneCommandeBeton {
  id:                       string;
  commandeId:               string;
  produitId:                string;
  quantite:                 string;
  prixUnitaireFcfa:         string;
  remiseLignePct:           string;
  totalLigneFcfa:           string;
  delaiCureJoursSnapshot:   number | null;
  notes:                    string | null;
  createdAt:                string;
  produit?: {
    id:             string;
    code:           string;
    designation:    string;
    uniteVente?:    string;
    poidsKg?:       string | null;
    delaiCureJours?: number | null;
  };
}

export interface Commande {
  id:                     string;
  filialeId:              string;
  reference:              string;
  clientId:               string;
  chantierId:             string | null;
  dateCommande:           string;
  dateLivraisonSouhaitee: string | null;
  dateLivraisonReelle:    string | null;
  statut:                 StatutCommande;
  remisePct:              string;
  montantHtFcfa:          string;
  poidsTotalKg:           string;
  commercialUserId:       string | null;
  raisonAnnulation:       string | null;
  notes:                  string | null;
  createdAt:              string;
  updatedAt:              string;
  client?:    { id: string; fullName: string; companyName: string | null; email?: string | null; phone?: string | null };
  chantier?:  { id: string; code: string; nom: string; adresse?: string | null } | null;
  lignes?:    LigneCommandeBeton[];
  ordres?:    Array<{ id: string; reference: string; statut: string; quantite: string }>;
  _count?:    { ordres: number };
}

export interface ProduitWithBom extends Produit {
  bomItems: ProduitBOMItem[];
  coutRevient: {
    coutRevientCents: number;
    margeCents:       number;
    margePct:         number;
  };
}

export const FAMILLE_LABELS: Record<FamilleProduit, string> = {
  POTEAUX_BA:        'Poteaux BA',
  AGGLOS:            'Agglos / Hourdis',
  VOIRIE_VRD:        'Voirie VRD',
  BETON_PRET_EMPLOI: 'Béton prêt à l\'emploi',
  AUTRE:             'Autre',
};

export const MACHINE_LABELS: Record<MachineCode, string> = {
  DONGYUE_QT18:    'Dongyue QT18-15',
  HAIYU_COFFRAGE:  'Haiyu coffrage',
  COFFRAGE_MANUEL: 'Coffrage manuel',
  TOUPIE_BPE:      'Camion toupie BPE',
  AUTRE:           'Autre',
};

// ── ORDRES DE FABRICATION (Sprint LB-B Production) ──────────────────────────

export type StatutOF = 'planifie' | 'en_cours' | 'termine' | 'annule';

export const STATUT_OF_LABELS: Record<StatutOF, string> = {
  planifie: 'Planifié',
  en_cours: 'En cours',
  termine:  'Terminé',
  annule:   'Annulé',
};

export interface OrdreFabrication {
  id:             string;
  filialeId:      string;
  reference:      string;
  produitId:      string;
  quantite:       string;     // Decimal sérialisé
  statut:         StatutOF;
  datePlanifiee:  string;
  dateDebut:      string | null;
  dateFin:        string | null;
  notes:          string | null;
  createdById:    string;
  createdAt:      string;
  updatedAt:      string;
  // ── Enrichissement préfa Phase 1.3 (brief §3) ──
  commandeId:             string | null;
  mouleId:                string | null;
  equipeProductionId:     string | null;
  ferraillageAt:          string | null;
  coffrageAt:             string | null;
  dateCoulage:            string | null;
  dateDecoffragePrevue:   string | null;
  dateDecoffrageReelle:   string | null;
  delaiCureJoursSnapshot: number | null;
  produit?: {
    id: string; code: string; designation: string;
    famille: FamilleProduit; uniteVente: string;
  };
  moule?:            { id: string; code: string; libelle: string; statut: StatutMoule; nbCyclesEffectues?: number; prochainEntretienCycles?: number | null } | null;
  equipeProduction?: { id: string; code: string; nom: string; chefNom: string | null; specialite?: string | null } | null;
  commande?:         { id: string; reference: string; clientId?: string; statut?: StatutCommande; dateLivraisonSouhaitee?: string | null } | null;
}

export interface OrdreFabricationDetail extends OrdreFabrication {
  produit: OrdreFabrication['produit'] & {
    bomItems: ProduitBOMItem[];
  };
  consommationMP: Array<{
    matiereId:        string;
    matiereCode:      string;
    matiereNom:       string;
    unite:            string;
    quantiteUnitaire: number;
    quantiteTotale:   number;
    stockDisponible:  number;
    suffisant:        boolean;
  }>;
}

// ── CHANTIERS ───────────────────────────────────────────────────────────────

export type StatutChantier = 'planifie' | 'en_cours' | 'suspendu' | 'termine' | 'annule';

export const STATUT_CHANTIER_LABELS: Record<StatutChantier, string> = {
  planifie: 'Planifié',
  en_cours: 'En cours',
  suspendu: 'Suspendu',
  termine:  'Terminé',
  annule:   'Annulé',
};

export interface Chantier {
  id:             string;
  filialeId:      string;
  code:           string;
  nom:            string;
  clientId:       string | null;
  adresse:        string | null;
  volumeTotal:    string;  // Decimal sérialisé
  volumeLivre:    string;
  dateDebut:      string;
  dateFinPrevue:  string | null;
  dateFinReelle:  string | null;
  statut:         StatutChantier;
  notes:          string | null;
  createdAt:      string;
  updatedAt:      string;
  // Sprint 4 — Module 9 (enrichissement)
  maitreOuvrage:          string | null;
  gpsLat:                 string | null;
  gpsLng:                 string | null;
  responsableNom:         string | null;
  responsableTel:         string | null;
  responsableEmail:       string | null;
  planInstallationPdfUrl: string | null;
  client?: { id: string; fullName: string; companyName: string | null } | null;
}

// ── LOTS DE PRODUCTION (LB-B v2) ────────────────────────────────────────────

export type StatutLot = 'en_production' | 'valide' | 'rejete';

export const STATUT_LOT_LABELS: Record<StatutLot, string> = {
  en_production: 'En production',
  valide:        'Validé',
  rejete:        'Rejeté',
};

export interface LotProduction {
  id:                string;
  filialeId:         string;
  ordreId:           string;
  lotNumber:         string;
  quantite:          string;  // Decimal sérialisé
  dateProduction:    string;
  statut:            StatutLot;
  controleQualiteOk: boolean | null;
  fc28Mesure:        string | null;  // Decimal sérialisé en string
  qrCode:            string;
  notes:             string | null;
  createdAt:         string;
  updatedAt:         string;
  // Sprint 2 — Module 4 (lien gâchée) + Module 6 (parc)
  gacheeId:           string | null;
  zoneParc:           string | null;
  emplacementParc:    string | null;
  dateEntreeParc:     string | null;
  dateSortieParc:     string | null;
  commandeReserveeId: string | null;
  commandeReservee?:  { id: string; reference: string; clientId?: string; statut?: string } | null;
}

export interface LotProductionDetail extends LotProduction {
  ordre: {
    id: string;
    reference: string;
    quantite: string;
    statut: StatutOF;
    datePlanifiee: string;
    produit: {
      id: string;
      code: string;
      designation: string;
      famille: FamilleProduit;
      norme: string | null;
      classeProduit: string | null;
      resistanceFc28: string | null;
    };
  };
}

// ── TRANSPORT MINIER (Phase 2 — Royal LT) ────────────────────────────────

export type StatutEquipe = 'actif' | 'inactif';
export const STATUT_EQUIPE_LABELS: Record<StatutEquipe, string> = {
  actif:   'Actif',
  inactif: 'Inactif',
};

export interface Equipe {
  id:         string;
  filialeId:  string;
  code:       string;
  nom:        string;
  chefNom:    string | null;
  secteur:    string | null;
  exploitant: string | null;
  statut:     StatutEquipe;
  notes:      string | null;
  createdAt:  string;
  updatedAt:  string;
  _count?:    { camions: number };
  camions?:   Array<{ id: string; code: string; immatriculation: string | null; marque: string | null; capaciteTonnes: string; statut: StatutCamion; chauffeurNom: string | null }>;
}

export type StatutCamion = 'disponible' | 'en_rotation' | 'maintenance' | 'hors_service';
export const STATUT_CAMION_LABELS: Record<StatutCamion, string> = {
  disponible:   'Disponible',
  en_rotation:  'En rotation',
  maintenance:  'Maintenance',
  hors_service: 'Hors service',
};

export interface Camion {
  id:                string;
  filialeId:         string;
  code:              string;
  immatriculation:   string | null;
  marque:            string | null;
  modele:            string | null;
  capaciteTonnes:    string;  // Decimal sérialisé
  typeCamion:        TypeCamion | null;
  chargeMaxTonnes:   string | null;
  consoTheoriqueL100km: string | null;
  chauffeurNom:      string | null;
  equipeId:          string | null;
  statut:            StatutCamion;
  kmActuel:          number | null;
  dateMiseEnService: string | null;
  prochainEntretien: string | null;
  notes:             string | null;
  actif:             boolean;
  createdAt:         string;
  updatedAt:         string;
  equipe?:           { id: string; code: string; nom: string; chefNom?: string | null } | null;
}

export type TypeCamion = 'benne_6x4' | 'benne_8x4' | 'tracteur' | 'x3000' | 'autre';
export const TYPE_CAMION_LABELS: Record<TypeCamion, string> = {
  benne_6x4: 'Benne 6×4',
  benne_8x4: 'Benne 8×4',
  tracteur:  'Tracteur',
  x3000:     'X3000',
  autre:     'Autre',
};

// ── Mines & Voyages (Sprint Royal LT A1) ─────────────────────────────────

export interface Mine {
  id:                     string;
  filialeId:              string;
  code:                   string;
  nom:                    string;
  exploitant:             string | null;
  portDestinationDefault: string | null;
  actif:                  boolean;
  notes:                  string | null;
  createdAt:              string;
  updatedAt:              string;
}

export type StatutVoyage = 'en_cours' | 'complete' | 'annule' | 'incident';
export const STATUT_VOYAGE_LABELS: Record<StatutVoyage, string> = {
  en_cours: 'En cours',
  complete: 'Complété',
  annule:   'Annulé',
  incident: 'Incident',
};
export const STATUT_VOYAGE_COLORS: Record<StatutVoyage, string> = {
  en_cours: '#3b82f6',
  complete: '#10b981',
  annule:   '#94a3b8',
  incident: '#ef4444',
};

export type TypeIncident = 'mecanique' | 'securite' | 'meteo' | 'piste' | 'surcharge' | 'autre';
export const TYPE_INCIDENT_LABELS: Record<TypeIncident, string> = {
  mecanique: 'Mécanique',
  securite:  'Sécurité',
  meteo:     'Météo',
  piste:     'Piste dégradée',
  surcharge: 'Surcharge',
  autre:     'Autre',
};

export interface Voyage {
  id:                  string;
  filialeId:           string;
  numero:              string;
  camionId:            string;
  chauffeurNom:        string;
  chefEquipeUserId:    string | null;
  mineId:              string;
  portDestination:     string;
  heureDepartMine:     string;
  tonnageCharge:       string; // Decimal
  numeroBonPesee:      string;
  kmDepart:            number | null;
  heureArriveePort:    string | null;
  heureDepartPort:     string | null;
  kmRetour:            number | null;
  statut:              StatutVoyage;
  incidentType:        TypeIncident | null;
  incidentDescription: string | null;
  raisonAnnulation:    string | null;
  notes:               string | null;
  createdAt:           string;
  updatedAt:           string;
  camion?:             Pick<Camion, 'id' | 'code' | 'immatriculation' | 'modele' | 'typeCamion' | 'chargeMaxTonnes'>;
  mine?:               Pick<Mine, 'id' | 'code' | 'nom' | 'exploitant' | 'portDestinationDefault'>;
}

// ── Carburant (Sprint Royal LT B1) ────────────────────────────────────────

export type RatioGraviteCarburant = 'faible' | 'normal' | 'eleve' | 'anomalie';
export const RATIO_GRAVITE_LABELS: Record<RatioGraviteCarburant, string> = {
  faible:   'Faible (<85%)',
  normal:   'Normal (85-115%)',
  eleve:    'Élevé (115-130%)',
  anomalie: 'Anomalie (>130%)',
};
export const RATIO_GRAVITE_COLORS: Record<RatioGraviteCarburant, string> = {
  faible:   '#facc15',
  normal:   '#10b981',
  eleve:    '#f59e0b',
  anomalie: '#dc2626',
};
export const RATIO_GRAVITE_ICONS: Record<RatioGraviteCarburant, string> = {
  faible:   '🟡',
  normal:   '✅',
  eleve:    '🟠',
  anomalie: '🔴',
};

export type StatutBonCarburant = 'valide' | 'annule';
export type GraviteAnomalieCarburant = 'faible' | 'modere' | 'grave';
export type StatutAnomalieCarburant = 'ouverte' | 'en_investigation' | 'resolue' | 'classee';

export const STATUT_ANOMALIE_LABELS: Record<StatutAnomalieCarburant, string> = {
  ouverte:          'Ouverte',
  en_investigation: 'En investigation',
  resolue:          'Résolue',
  classee:          'Classée',
};

export interface BonCarburant {
  id:                     string;
  filialeId:              string;
  numero:                 string;
  dateHeure:              string;
  mineId:                 string;
  agentCarburantUserId:   string | null;
  agentCarburantNom:      string | null;
  camionId:               string;
  chauffeurNom:           string;
  kmCompteur:             number;
  qteDemandeeL:           string; // Decimal
  qteServieL:             string;
  raisonEcart:            string | null;
  kmDepuisDernierPlein:   number | null;
  consoTheoriqueL:        string | null;
  consoReelleL:           string | null;
  ratioPct:               string | null;
  gravite:                RatioGraviteCarburant | null;
  photoCompteurUrl:       string | null;
  signedByChauffeur:      boolean;
  signedByAgent:          boolean;
  statut:                 StatutBonCarburant;
  notes:                  string | null;
  createdAt:              string;
  updatedAt:              string;
  camion?:                { id: string; code: string; immatriculation: string | null; modele: string | null; consoTheoriqueL100km: string | null };
  mine?:                  { id: string; code: string; nom: string };
  anomalie?:              { id: string; gravite: GraviteAnomalieCarburant; statut: StatutAnomalieCarburant; estRecidive: boolean } | null;
}

export interface AnomalieCarburant {
  id:                        string;
  filialeId:                 string;
  bonCarburantId:            string;
  gravite:                   GraviteAnomalieCarburant;
  ratioConstate:             string;
  statut:                    StatutAnomalieCarburant;
  assignedToUserId:          string | null;
  commentairesInvestigation: string | null;
  decisionFinale:            string | null;
  estRecidive:               boolean;
  resoluAt:                  string | null;
  createdAt:                 string;
  updatedAt:                 string;
  bonCarburant?:             BonCarburant;
}

// ── Planning préventif (Sprint Royal LT C2) ────────────────────────────────

export type StatutPlanningPreventif = 'planifie' | 'genere' | 'realise' | 'annule';
export const STATUT_PLANNING_LABELS: Record<StatutPlanningPreventif, string> = {
  planifie: 'Planifié',
  genere:   'BT généré',
  realise:  'Réalisé',
  annule:   'Annulé',
};
export const NIVEAU_COLORS: Record<string, string> = {
  n1_quotidien: '#94a3b8',
  n2_hebdo:     '#3b82f6',
  n3_10000km:   '#f59e0b',
  n4_50000km:   '#dc2626',
  n5_annuel:    '#a855f7',
};

export interface AlerteMaintenance {
  camionId:           string;
  camionCode:         string;
  immat:              string | null;
  modele:             string | null;
  niveau:             import('./index').NiveauBonTravail;
  raison:             string;
  kmActuel:           number | null;
  kmEcheance:         number | null;
  joursRestants:      number | null;
  gravite:            'critique' | 'important' | 'info';
  planningExisteDeja: boolean;
}

export interface PlanningPreventif {
  id:            string;
  filialeId:     string;
  camionId:      string;
  niveau:        import('./index').NiveauBonTravail;
  datePrevue:    string;
  kmPrevu:       number | null;
  lieuSuggere:   import('./index').LieuBonTravail | null;
  statut:        StatutPlanningPreventif;
  bonTravailId:  string | null;
  source:        string | null;
  notes:         string | null;
  createdAt:     string;
  updatedAt:     string;
  camion?:       { id: string; code: string; immatriculation: string | null; modele: string | null };
  bonTravail?:   { id: string; numero: string; statut: string } | null;
}

// ── Maintenance (Sprint Royal LT C1) ───────────────────────────────────────

export type TypeBonTravail     = 'preventif' | 'curatif' | 'accident' | 'amelioration';
export type NiveauBonTravail   = 'n1_quotidien' | 'n2_hebdo' | 'n3_10000km' | 'n4_50000km' | 'n5_annuel';
export type LieuBonTravail     = 'garage_central' | 'atelier_mine_1' | 'atelier_mine_2' | 'atelier_mine_3' | 'camion_atelier_mobile';
export type CauseBonTravail    = 'usure_normale' | 'surcharge' | 'piste_degradee' | 'defaut_piece' | 'erreur_conducteur' | 'inconnue' | 'autre';
export type PrioriteBonTravail = 'normale' | 'haute' | 'critique';
export type StatutBonTravail   = 'ouvert' | 'en_cours' | 'en_attente' | 'termine' | 'valide' | 'annule';

export const TYPE_BT_LABELS:     Record<TypeBonTravail, string>     = { preventif: 'Préventif', curatif: 'Curatif', accident: 'Accident', amelioration: 'Amélioration' };
export const NIVEAU_BT_LABELS:   Record<NiveauBonTravail, string>   = { n1_quotidien: 'N1 — Quotidien', n2_hebdo: 'N2 — Hebdomadaire', n3_10000km: 'N3 — /10 000 km', n4_50000km: 'N4 — /50 000 km', n5_annuel: 'N5 — Annuel' };
export const LIEU_BT_LABELS:     Record<LieuBonTravail, string>     = { garage_central: 'Garage central', atelier_mine_1: 'Atelier Mine 1', atelier_mine_2: 'Atelier Mine 2', atelier_mine_3: 'Atelier Mine 3', camion_atelier_mobile: 'Camion-atelier mobile' };
export const CAUSE_BT_LABELS:    Record<CauseBonTravail, string>    = { usure_normale: 'Usure normale', surcharge: 'Surcharge', piste_degradee: 'Piste dégradée', defaut_piece: 'Défaut pièce', erreur_conducteur: 'Erreur conducteur', inconnue: 'Inconnue', autre: 'Autre' };
export const PRIORITE_BT_LABELS: Record<PrioriteBonTravail, string> = { normale: 'Normale', haute: 'Haute', critique: 'Critique (<2h)' };
export const STATUT_BT_LABELS:   Record<StatutBonTravail, string>   = { ouvert: 'Ouvert', en_cours: 'En cours', en_attente: 'En attente', termine: 'Terminé', valide: 'Validé', annule: 'Annulé' };
export const STATUT_BT_COLORS:   Record<StatutBonTravail, string>   = { ouvert: '#94a3b8', en_cours: '#3b82f6', en_attente: '#f59e0b', termine: '#a855f7', valide: '#10b981', annule: '#cbd5e1' };

export interface LignePieceBT {
  id:               string;
  bonTravailId:     string;
  designation:      string;
  reference:        string | null;
  qte:              string;
  prixUnitaireFcfa: string;
  totalFcfa:        string;
  notes:            string | null;
  createdAt:        string;
}

export interface BonTravail {
  id:                       string;
  filialeId:                string;
  numero:                   string;
  camionId:                 string;
  signaleParUserId:         string | null;
  signaleParNom:            string | null;
  type:                     TypeBonTravail;
  niveau:                   NiveauBonTravail;
  lieu:                     LieuBonTravail;
  descriptionPanne:         string;
  cause:                    CauseBonTravail | null;
  mecanicienPrincipalNom:   string;
  mecaniciensAdditionnels:  string[];
  chefParcNom:              string | null;
  priorite:                 PrioriteBonTravail;
  heureDebut:               string | null;
  heureFin:                 string | null;
  tempsPasseMinutes:        number | null;
  tauxHoraireFcfa:          string | null;
  coutMainOeuvreFcfa:       string | null;
  coutPiecesFcfa:           string | null;
  coutExterneFcfa:          string | null;
  coutTotalFcfa:            string | null;
  travauxEffectues:         string | null;
  testEffectue:             boolean;
  testDescription:          string | null;
  remiseEnServiceAt:        string | null;
  kilometrageClotureKm:     number | null;
  photosUrls:               string[];
  signedByMecanicien:       boolean;
  signedByChefParc:         boolean;
  statut:                   StatutBonTravail;
  raisonAnnulation:         string | null;
  notes:                    string | null;
  createdAt:                string;
  updatedAt:                string;
  camion?:                  { id: string; code: string; immatriculation: string | null; modele: string | null; kmActuel?: number | null };
  lignes?:                  LignePieceBT[];
}

export interface RapportMensuelMaintenance {
  filiale: { name: string; fullName: string; pays: string; devise: string };
  periode: { from: string; to: string; mois: string };
  btParType: Array<{ type: TypeBonTravail; count: number; coutFcfa: number }>;
  statsCouts: {
    coutTotalFcfa:           number;
    coutMoyenParCamionFcfa:  number;
    nbCamionsAvecBT:         number;
    coutMoisPrecedentFcfa:   number | null;
    evolutionPct:            number | null;
  };
  topPannesRecurrentes: Array<{
    designation:        string;
    nbCamionsDistincts: number;
    nbFois:             number;
    coutCumulFcfa:      number;
  }>;
  topPiecesConsommees: Array<{
    designation:    string;
    nbFois:         number;
    quantiteTotale: number;
    coutCumulFcfa:  number;
  }>;
  tauxRespectPreventif: {
    totalEntries: number;
    realises:     number;
    pourcentage:  number;
  };
  camionsCoutTonneAnormal: Array<{
    camionCode:    string;
    immat:         string | null;
    modele:        string | null;
    coutTotalFcfa: number;
    tonnesMois:    number;
    coutParTonne:  number;
    gravite:       'reforme' | 'analyse';
  }>;
  generatedAt: string;
}

export interface MaintenanceDashboard {
  date:    string;
  periode: { from: string; to: string };
  kpis: {
    btOuverts:          number;
    btCeMois:           number;
    coutMoisFcfa:       number;
    tauxDispoFlottePct: number;
  };
  alertes: Array<{ niveau: 'critique' | 'important' | 'info'; icone: string; texte: string }>;
  topCoutsCamions: Array<{
    camionCode:    string;
    immat:         string | null;
    modele:        string | null;
    nbBT:          number;
    coutTotalFcfa: number;
    coutParTonne:  number | null;
  }>;
  parStatut:  Array<{ statut: string; count: number }>;
  updatedAt:  string;
}

export type TypeMouvementDepot = 'approvisionnement' | 'consommation' | 'ajustement';
export const TYPE_MOUVEMENT_LABELS: Record<TypeMouvementDepot, string> = {
  approvisionnement: 'Approvisionnement',
  consommation:      'Consommation',
  ajustement:        'Ajustement',
};

export interface DepotCarburant {
  id:              string;
  mineId:          string;
  mineCode:        string;
  mineNom:         string;
  capaciteTotaleL: number | null;
  responsableNom:  string | null;
  actif:           boolean;
  niveauEstimeL:   number;
  consoMoyJourL:   number;
  autonomieJours:  number | null;
  alerteNiveauBas: boolean;
}

export interface MouvementDepot {
  id:              string;
  filialeId:       string;
  depotId:         string;
  type:            TypeMouvementDepot;
  volumeL:         string;
  dateEffet:       string;
  source:          string | null;
  bonCarburantId:  string | null;
  createdByUserId: string | null;
  notes:           string | null;
  createdAt:       string;
  depot?:          { id: string; mine: { code: string; nom: string } };
}

export interface RapportMensuelCarburant {
  filiale: { name: string; fullName: string; pays: string; devise: string };
  periode: { from: string; to: string; mois: string };
  totauxParDepot: Array<{ code: string; nom: string; litres: number; bons: number }>;
  consoMoyL100kmFlotte:  number | null;
  consoMoyMoisPrecedent: number | null;
  evolutionPct:          number | null;
  topSurConsommateurs: Array<{
    camionCode:    string;
    immat:         string | null;
    modele:        string | null;
    nbBons:        number;
    litres:        number;
    ratioMoyen:    number;
    sumEcartL:     number;
  }>;
  topAnomalies: Array<{
    bonNumero:      string;
    camionCode:     string;
    chauffeur:      string;
    mineCode:       string;
    ratioPct:       number;
    gravite:        string;
    decisionFinale: string | null;
    estRecidive:    boolean;
    date:           string;
  }>;
  recidivistes:  Array<{ camionCode: string; nbRecidives: number }>;
  economiesPotentielles: { litres: number; fcfa: number; prixLitre: number };
  generatedAt:   string;
}

export interface CarburantDashboard {
  date:    string;
  periode: { from: string; to: string };
  kpis: {
    bonsMois:             number;
    litresMois:           number;
    anomaliesMois:        number;
    consoMoyL100kmFlotte: number | null;
  };
  alertes: Array<{
    niveau: 'critique' | 'important' | 'info';
    icone:  string;
    texte:  string;
  }>;
  evolution30j: Array<{ date: string; litres: number }>;
  parDepot: Array<{
    code:   string;
    nom:    string;
    litres: number;
    bons:   number;
  }>;
  anomaliesOuvertes: Array<{
    id:           string;
    bonNumero:    string;
    camionCode:   string;
    mineCode:     string;
    chauffeur:    string;
    ratioPct:     number;
    gravite:      string;
    estRecidive:  boolean;
    statut:       string;
    createdAt:    string;
  }>;
  updatedAt: string;
}

// ── Rapport — Destinataires + Envoi (Sprint A2.2) ──────────────────────────

export interface RapportDestinataire {
  id:        string;
  filialeId: string;
  email:     string;
  fullName:  string | null;
  role:      string | null;
  actif:     boolean;
  createdAt: string;
  updatedAt: string;
}

export type StatutEnvoiRapport = 'success' | 'failed' | 'dry_run';
export const STATUT_ENVOI_LABELS: Record<StatutEnvoiRapport, string> = {
  success: 'Envoyé',
  failed:  'Échec',
  dry_run: 'Dry-run (SMTP non configuré)',
};
export const STATUT_ENVOI_COLORS: Record<StatutEnvoiRapport, string> = {
  success: '#16a34a',
  failed:  '#dc2626',
  dry_run: '#f59e0b',
};

export interface RapportEnvoiLog {
  id:            string;
  filialeId:     string;
  dateRapport:   string;
  sentAt:        string;
  sentByUserId:  string | null;
  destinataires: string[];
  subject:       string | null;
  statut:        StatutEnvoiRapport;
  errorMessage:  string | null;
  source:        string | null;
}

export interface RapportJournalier {
  date: string;
  filiale: { name: string; fullName: string; pays: string; devise: string; logoPath: string | null };
  syntheseOperationnelle: Array<{
    indicateur: string;
    realise:    number | string;
    objectif:   number | string;
    ecart:      string;
    ok:         boolean;
  }>;
  detailParMine: Array<{
    code:    string;
    nom:     string;
    voyages: number;
    tonnes:  number;
    camionsActifs: number;
    incidents:     number;
  }>;
  revenusEstimes: {
    tonnes:          number;
    usd:             number;
    fcfa:            number;
    tauxFcfa:        number;
    prixUsdParTonne: number;
  };
  incidents: Array<{
    voyageId:    string;
    numero:      string;
    camion:      string;
    chauffeur:   string;
    mine:        string;
    type:        string | null;
    description: string | null;
    heure:       string;
  }>;
  generatedAt: string;
}

export interface TransportDashboard {
  date: string;
  kpis: {
    camionsActifs:      { value: number; total: number; ratio: number };
    voyagesJour:        { realises: number; objectif: number; ratio: number };
    tonnesJour:         { value: number; hier: number; mois: number };
    revenusEstimesJour: { fcfa: number; usd: number; tauxFcfa: number };
  };
  alertes: Array<{
    niveau: 'critique' | 'important' | 'info';
    icone:  string;
    texte:  string;
    heure:  string;
  }>;
  repartitionParMine: Array<{
    mineId:  string;
    code:    string;
    nom:     string;
    voyages: number;
    tonnes:  number;
  }>;
  statutFlotteParSite: Array<{
    mineId:          string | null;
    code:            string;
    nom:             string;
    camionsAffectes: number;
    operationnels:   number;
    enMaintenance:   number;
    enPanne:         number;
    tauxDispo:       number;
    voyagesToday:    number;
    tonnesToday:     number;
  }>;
  voyagesParTrancheHoraire: Array<{
    tranche: string;
    par:     Record<string, number>;
    total:   number;
  }>;
  liveFeed: Array<{
    type:    'voyage_complete' | 'panne' | 'maintenance' | 'incident' | 'carburant_anormal';
    icone:   string;
    heure:   string;
    texte:   string;
    voyageId?: string;
    camionId?: string;
  }>;
  updatedAt: string;
}

// ── TELECOM WIFI (Phase 2 — Sinergie WeFly) ──────────────────────────────

export type StatutHotspot = 'actif' | 'maintenance' | 'hors_service';
export const STATUT_HOTSPOT_LABELS: Record<StatutHotspot, string> = {
  actif:        'Actif',
  maintenance:  'Maintenance',
  hors_service: 'Hors service',
};

export interface Hotspot {
  id:                   string;
  filialeId:            string;
  code:                 string;
  nom:                  string;
  adresse:              string | null;
  quartier:             string | null;
  ville:                string | null;
  latitude:             string | null;   // Decimal sérialisé
  longitude:            string | null;
  statut:               StatutHotspot;
  dateInstallation:     string | null;
  derniereMaintenance:  string | null;
  capaciteUtilisateurs: number | null;
  fournisseurInternet:  string | null;
  bandwidthMbps:        number | null;
  notes:                string | null;
  actif:                boolean;
  createdAt:            string;
  updatedAt:            string;
  _count?:              { abonnes: number; transactions: number };
}

export type StatutAbonne = 'actif' | 'expire' | 'suspendu';
export const STATUT_ABONNE_LABELS: Record<StatutAbonne, string> = {
  actif:    'Actif',
  expire:   'Expiré',
  suspendu: 'Suspendu',
};

export interface Abonne {
  id:                string;
  filialeId:         string;
  msisdn:            string;
  nom:               string | null;
  email:             string | null;
  hotspotPrefereId:  string | null;
  planAbonnement:    string | null;
  statut:            StatutAbonne;
  dateInscription:   string;
  derniereConnexion: string | null;
  soldeDataMo:       number;
  totalDepenseCents: string;  // BigInt sérialisé
  notes:             string | null;
  createdAt:         string;
  updatedAt:         string;
  hotspotPrefere?:   { id: string; code: string; nom: string; quartier: string | null } | null;
}

export type OperateurMobile = 'orange' | 'mtn' | 'moov' | 'wave';
export const OPERATEUR_LABELS: Record<OperateurMobile, string> = {
  orange: 'Orange Money',
  mtn:    'MTN MoMo',
  moov:   'Moov Money',
  wave:   'Wave',
};

export type StatutTransaction = 'en_attente' | 'validee' | 'echec' | 'remboursee';
export const STATUT_TRANSACTION_LABELS: Record<StatutTransaction, string> = {
  en_attente: 'En attente',
  validee:    'Validée',
  echec:      'Échec',
  remboursee: 'Remboursée',
};

export interface TransactionMobileMoney {
  id:              string;
  filialeId:       string;
  abonneId:        string | null;
  hotspotId:       string | null;
  reference:       string;
  operateur:       OperateurMobile;
  msisdn:          string;
  montantCents:    string;  // BigInt sérialisé
  dataAchetMo:     number | null;
  statut:          StatutTransaction;
  dateTransaction: string;
  notes:           string | null;
  createdAt:       string;
  updatedAt:       string;
  abonne?:         { id: string; msisdn: string; nom: string | null } | null;
  hotspot?:        { id: string; code: string; nom: string; quartier: string | null } | null;
}

// ── IMMOBILIER (Phase 2 — David & Co / DCO) ──────────────────────────────

export type StatutProgramme = 'prospection' | 'en_cours' | 'livre' | 'suspendu' | 'annule';
export const STATUT_PROGRAMME_LABELS: Record<StatutProgramme, string> = {
  prospection: 'Prospection',
  en_cours:    'En cours',
  livre:       'Livré',
  suspendu:    'Suspendu',
  annule:      'Annulé',
};

export type StatutLotImmo = 'disponible' | 'reserve' | 'vendu' | 'bloque';
export const STATUT_LOT_IMMO_LABELS: Record<StatutLotImmo, string> = {
  disponible: 'Disponible',
  reserve:    'Réservé',
  vendu:      'Vendu',
  bloque:     'Bloqué',
};

export type TypeBien = 'appartement' | 'villa' | 'maison' | 'studio' | 'parcelle' | 'local_commercial' | 'bureau';
export const TYPE_BIEN_LABELS: Record<TypeBien, string> = {
  appartement:      'Appartement',
  villa:            'Villa',
  maison:           'Maison',
  studio:           'Studio',
  parcelle:         'Parcelle',
  local_commercial: 'Local commercial',
  bureau:           'Bureau',
};

export type StatutReservationVefa = 'en_cours' | 'signee' | 'acomptes_partiels' | 'acompte_complete' | 'acte_authentique' | 'annulee';
export const STATUT_RESERVATION_VEFA_LABELS: Record<StatutReservationVefa, string> = {
  en_cours:           'En cours',
  signee:             'Signée',
  acomptes_partiels:  'Acomptes partiels',
  acompte_complete:   'Acompte complet',
  acte_authentique:   'Vente définitive',
  annulee:            'Annulée',
};

export interface Programme {
  id:                  string;
  filialeId:           string;
  code:                string;
  nom:                 string;
  typeProgramme:       string | null;
  adresse:             string | null;
  quartier:            string | null;
  ville:               string | null;
  surfaceTerrainM2:    string | null;  // Decimal sérialisé
  budgetTotalCents:    string;          // BigInt sérialisé
  budgetDepenseCents:  string;
  dateDebut:           string | null;
  dateLivraisonPrevue: string | null;
  statut:              StatutProgramme;
  nbLotsTotal:         number | null;
  notes:               string | null;
  createdAt:           string;
  updatedAt:           string;
  _count?:             { lots: number };
  lots?:               Array<{ id: string; code: string; designation: string | null; typeBien: TypeBien; prixVenteCents: string; statut: StatutLotImmo }>;
}

export interface LotImmo {
  id:              string;
  filialeId:       string;
  programmeId:     string | null;
  code:            string;
  designation:     string | null;
  typeBien:        TypeBien;
  surfaceUtileM2:  string | null;
  surfaceTotaleM2: string | null;
  nbPieces:        number | null;
  etage:           number | null;
  prixVenteCents:  string;
  statut:          StatutLotImmo;
  notes:           string | null;
  createdAt:       string;
  updatedAt:       string;
  programme?:      { id: string; code: string; nom: string } | null;
}

export interface ReservationVefa {
  id:                 string;
  filialeId:          string;
  lotId:              string;
  clientId:           string | null;
  reference:          string;
  prixVenteCents:     string;
  acompteCumuleCents: string;
  statut:             StatutReservationVefa;
  dateReservation:    string;
  dateSignature:      string | null;
  notes:              string | null;
  createdAt:          string;
  updatedAt:          string;
  lot?:               { id: string; code: string; designation: string | null; typeBien: TypeBien; programme?: { code: string; nom: string } | null } | null;
  client?:            { id: string; fullName: string; companyName: string | null } | null;
}

// ── ALERTES BUSINESS (LB-E) ──────────────────────────────────────────────

export type SeveriteAlerte = 'info' | 'warning' | 'danger';

export interface Alerte {
  type:       string;
  severite:   SeveriteAlerte;
  titre:      string;
  detail:     string;
  entityRef?: string;
  entityId?:  string;
  date?:      string;
}

export interface AlertesResult {
  alertes: Alerte[];
  counts:  Record<SeveriteAlerte, number>;
}

// ── TARIFS CONTRATS CADRE (LB-D) ─────────────────────────────────────────

export type StatutTarif = 'actif' | 'expire' | 'resilie';

export const STATUT_TARIF_LABELS: Record<StatutTarif, string> = {
  actif:   'Actif',
  expire:  'Expiré',
  resilie: 'Résilié',
};

export interface TarifContratCadre {
  id:          string;
  filialeId:   string;
  clientId:    string;
  produitId:   string;
  contratRef:  string;
  prixHTCents: string;  // BigInt sérialisé
  remisePct:   string | null;
  dateDebut:   string;
  dateFin:     string | null;
  statut:      StatutTarif;
  notes:       string | null;
  createdAt:   string;
  updatedAt:   string;
  client?:     { id: string; fullName: string; companyName: string | null };
  produit?:    { id: string; code: string; designation: string; famille: FamilleProduit; prixVenteCents?: string };
}

export interface PrixApplicable {
  source:      'contrat_cadre' | 'catalogue';
  contratRef:  string | null;
  prixHTCents: number;
  remisePct:   number;
}

// ── DNC (Déclarations Non-Conformité — LB-C) ─────────────────────────────

export type StatutDNC = 'ouverte' | 'en_traitement' | 'cloturee' | 'annulee';

export const STATUT_DNC_LABELS: Record<StatutDNC, string> = {
  ouverte:       'Ouverte',
  en_traitement: 'En traitement',
  cloturee:      'Clôturée',
  annulee:       'Annulée',
};

export interface DNC {
  id:                string;
  filialeId:         string;
  lotId:             string | null;
  reference:         string;
  cause:             string;
  actionCorrective:  string | null;
  statut:            StatutDNC;
  dateOuverture:     string;
  dateCloture:       string | null;
  responsableId:    string | null;
  createdAt:        string;
  updatedAt:        string;
  lot?: { id: string; lotNumber: string; ordreId: string } | null;
}

// ── NF C 67-200 conformité (LB-C) ────────────────────────────────────────

export interface ConformiteNFResult {
  conforme:             boolean;
  raisonsNonConformite: string[];
  details: {
    fc28OK?:         boolean;
    longueurOK?:     boolean;
    chargeRuptureOK?: boolean;
  };
}

// ── PLANNING PRODUCTION ─────────────────────────────────────────────────────

export interface PlanningJour {
  date:         string;  // YYYY-MM-DD
  cmd:          number;  // nombre d'OF
  volumeM3:     number;
  capacitePct:  number;
}

export interface Planning {
  capaciteJournaliereM3: number;
  dateFrom:              string;
  dateTo:                string;
  days:                  PlanningJour[];
}

// ── TOUPIES (Flotte BPE) ────────────────────────────────────────────────────

export type StatutToupie = 'disponible' | 'en_livraison' | 'maintenance' | 'hors_service';

export const STATUT_TOUPIE_LABELS: Record<StatutToupie, string> = {
  disponible:   'Disponible',
  en_livraison: 'En livraison',
  maintenance:  'Maintenance',
  hors_service: 'Hors service',
};

export interface Toupie {
  id:                string;
  filialeId:         string;
  code:              string;
  immatriculation:   string | null;
  marque:            string | null;
  capaciteM3:        string;  // Decimal sérialisé
  statut:            StatutToupie;
  dateMiseEnService: string | null;
  prochainEntretien: string | null;
  notes:             string | null;
  actif:             boolean;
  createdAt:         string;
  updatedAt:         string;
}

// ── JOURNAL COMPTABLE (SYSCOHADA) ────────────────────────────────────────────

export type JournalType = 'OD' | 'VE' | 'VA' | 'BQ' | 'CA';
export type JournalEntryStatus = 'draft' | 'posted' | 'cancelled';

export interface Account {
  id:           string;
  code:         string;       // ex: '411000'
  label:        string;       // ex: 'Clients'
  accountClass: string;       // '1'..'7'
  accountType:  string;       // 'actif' | 'passif' | 'charge' | 'produit'
}

export interface JournalEntryLine {
  id:             string;
  entryId:        string;
  accountId:      string;
  filialeId:      string;
  label:          string;
  debitCents:     string;     // BigInt sérialisé en string
  creditCents:    string;
  currency:       Currency;
  amountFcfaCents: string | null;
  lineNumber:     number;
  account?: {                 // injecté via Prisma include
    code:  string;
    label: string;
  };
}

export interface JournalEntry {
  id:           string;
  filialeId:    string;
  journalType:  JournalType;
  reference:    string;
  entryDate:    string;       // ISO date
  description:  string;
  status:       JournalEntryStatus;
  createdById:  string;
  validatedById: string | null;
  postedAt:     string | null;
  /** Lot 2, étape 2.3 : numéro définitif attribué à la validation (ex. VE-2026-000001). */
  numero?:        number | null;
  numeroPiece?:   string | null;
  /** Écriture contre-passée par celle-ci. */
  contrePasseId?: string | null;
  createdAt:    string;
  updatedAt:    string;
  lines:        JournalEntryLine[];
  createdBy?:   { id: string; fullName: string };
  validatedBy?: { id: string; fullName: string } | null;
}

export const JOURNAL_TYPE_LABELS: Record<JournalType, string> = {
  OD: 'Opérations diverses',
  VE: 'Ventes',
  VA: 'Achats',
  BQ: 'Banque',
  CA: 'Caisse',
};

export const STATUS_LABELS: Record<JournalEntryStatus, string> = {
  draft:     'Brouillon',
  posted:    'Validée',
  cancelled: 'Annulée',
};

// ── FACTURES & CONTACTS ─────────────────────────────────────────────────────

export type InvoiceType = 'client' | 'supplier';
export type InvoiceStatus = 'proforma' | 'draft' | 'sent' | 'partial' | 'paid' | 'overdue' | 'cancelled';
export type ContactType = 'client' | 'supplier' | 'prospect';

export interface InvoiceLine {
  id:                string;
  invoiceId:         string;
  produitId:         string | null;
  designation:       string;
  quantite:          string;  // Decimal sérialisé
  prixUnitaireCents: string;  // BigInt sérialisé
  totalCents:        string;
  lineNumber:        number;
  createdAt:         string;
  updatedAt:         string;
  produit?: { id: string; code: string; designation: string } | null;
}

export interface Contact {
  id:          string;
  filialeId:   string;
  contactType: ContactType;
  companyName: string | null;
  fullName:    string;
  email:       string | null;
  phone:       string | null;
  address:     string | null;
  pays:        string | null;
  isActive:    boolean;
  // Ticket SIMGROUP-2026-003 §3.2 — mentions légales client OHADA
  rccmClient:  string | null;
  nccClient:   string | null;
  createdAt:   string;
  updatedAt:   string;
}

export interface Invoice {
  id:               string;
  filialeId:        string;
  invoiceType:      InvoiceType;
  reference:        string;
  contactId:        string;
  issueDate:        string;
  dueDate:          string;
  subtotalCents:    string;  // BigInt sérialisé
  taxCents:         string;
  totalCents:       string;
  paidAmountCents:  string;
  currency:         Currency;
  status:           InvoiceStatus;
  journalEntryId:   string | null;
  paidAt:           string | null;
  remisePct:        string;   // Decimal sérialisé (ex: "5.00")
  version:          number;   // 1 à la création, incrémenté à chaque update
  paymentTerms:     string | null; // Conditions de paiement libres (proforma)
  // Ticket SIMGROUP-2026-001 (MANQUE-01) — annulation proforma/draft
  cancelReason:     string | null;
  cancelledAt:      string | null;
  cancelledById:    string | null;
  createdAt:        string;
  updatedAt:        string;
  lines:            InvoiceLine[];
  contact?: {
    id:          string;
    fullName:    string;
    companyName: string | null;
    email?:      string | null;
  };
}

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  client:   'Client',
  supplier: 'Fournisseur',
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  proforma:  'Proforma',
  draft:     'Brouillon',
  sent:      'Envoyée',
  partial:   'Partielle',
  paid:      'Payée',
  overdue:   'En retard',
  cancelled: 'Annulée',
};

// ── RH (Personnel) ──────────────────────────────────────────────────────────

export type ContractType   = 'CDI' | 'CDD' | 'Stage' | 'Consultant';
export type EmployeeStatus = 'active' | 'suspended' | 'terminated';

export interface Employee {
  id:           string;
  filialeId:    string;
  matricule:    string;
  fullName:     string;
  email:        string | null;
  phone:        string | null;
  department:   string;
  jobTitle:     string;
  contractType: ContractType;
  hireDate:     string;
  endDate:      string | null;
  salaryCents:  string | null;  // BigInt sérialisé, null si user pas DAF (masqué)
  currency:     Currency;
  status:       EmployeeStatus;
  /** PAIE-1 (19/10/2026) : identité utile aux attestations et certificats (facultatif). */
  civilite?:      'M.' | 'Mme' | null;
  dateNaissance?: string | null;
  lieuNaissance?: string | null;
  numeroCnps?:    string | null;
  /** PAIE-2 : dossier de paie ; virement visible du DAF et du PDG seulement. */
  situationFamiliale?:  'celibataire' | 'marie' | 'divorce' | 'veuf' | null;
  enfantsACharge?:      number | null;
  categorieConvention?: string | null;
  soumisCnps?:          boolean;
  dateAnciennete?:      string | null;
  banqueNom?:           string | null;
  compteIntitule?:      string | null;
  compteNumero?:        string | null;
  createdAt:    string;
  updatedAt:    string;
}

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  CDI:        'CDI',
  CDD:        'CDD',
  Stage:      'Stage',
  Consultant: 'Consultant',
};

export const EMPLOYEE_STATUS_LABELS: Record<EmployeeStatus, string> = {
  active:     'Actif',
  suspended:  'Suspendu',
  terminated: 'Terminé',
};

// ── CRM Pipeline ────────────────────────────────────────────────────────────

export type PipelineStage = 'prospect' | 'qualification' | 'proposition' | 'négociation' | 'gagnée' | 'perdue';

export const PIPELINE_STAGES: PipelineStage[] = [
  'prospect', 'qualification', 'proposition', 'négociation', 'gagnée', 'perdue',
];

export const STAGE_LABELS: Record<PipelineStage, string> = {
  prospect:    'Prospect',
  qualification: 'Qualification',
  proposition: 'Proposition',
  'négociation': 'Négociation',
  'gagnée':    'Gagnée',
  perdue:      'Perdue',
};

/** Stage suivant dans le pipeline (pour bouton "Avancer") — null si terminal */
export const NEXT_STAGE: Record<PipelineStage, PipelineStage | null> = {
  prospect:    'qualification',
  qualification: 'proposition',
  proposition: 'négociation',
  'négociation': 'gagnée',
  'gagnée':    null,
  perdue:      null,
};

export interface Opportunity {
  id:                string;
  filialeId:         string;
  contactId:         string;
  title:             string;
  amountCents:       string;
  currency:          Currency;
  stage:             PipelineStage;
  probabilityPct:    number;
  expectedCloseDate: string | null;
  assignedToId:      string | null;
  notes:             string | null;
  createdAt:         string;
  updatedAt:         string;
  contact?:    { id: string; fullName: string; companyName: string | null };
  assignedTo?: { id: string; fullName: string } | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// TEXTPRINT (Phase 2 — Imprimerie + Textile)
// ═══════════════════════════════════════════════════════════════════════════

export type TypeMachine = 'traceur' | 'presse_numerique' | 'duplicateur' | 'dtf' | 'broderie' | 'presse_chaud' | 'autre';
export const TYPE_MACHINE_LABELS: Record<TypeMachine, string> = {
  traceur:          'Traceur grand format',
  presse_numerique: 'Presse numérique',
  duplicateur:      'Duplicateur',
  dtf:              'Imprimante DTF',
  broderie:         'Machine broderie',
  presse_chaud:     'Presse à chaud',
  autre:            'Autre',
};

export type StatutMachineImpression = 'operationnelle' | 'en_maintenance' | 'en_panne' | 'hors_service';
export const STATUT_MACHINE_LABELS: Record<StatutMachineImpression, string> = {
  operationnelle:  'Opérationnelle',
  en_maintenance:  'En maintenance',
  en_panne:        'En panne',
  hors_service:    'Hors service',
};

export type StatutCommandeImpression = 'confirmee' | 'en_production' | 'controle_qualite' | 'prete' | 'livree' | 'facturee' | 'soldee' | 'annulee';
export const STATUT_COMMANDE_IMPRESSION_LABELS: Record<StatutCommandeImpression, string> = {
  confirmee:        'Confirmée',
  en_production:    'En production',
  controle_qualite: 'Contrôle qualité',
  prete:            'Prête',
  livree:           'Livrée',
  facturee:         'Facturée',
  soldee:           'Soldée',
  annulee:          'Annulée',
};

export type PrioriteOF = 'normale' | 'urgente' | 'express';
export const PRIORITE_OF_LABELS: Record<PrioriteOF, string> = {
  normale: 'Normale',
  urgente: 'Urgente',
  express: 'Express',
};

export type StatutOFImpression = 'en_attente' | 'en_cours' | 'controle_qualite' | 'termine' | 'annule';
export const STATUT_OF_IMPRESSION_LABELS: Record<StatutOFImpression, string> = {
  en_attente:       'En attente',
  en_cours:         'En cours',
  controle_qualite: 'Contrôle qualité',
  termine:          'Terminé',
  annule:           'Annulé',
};

export type TypeConsommable = 'encre' | 'papier' | 'toner' | 'tissu' | 'fil_broderie' | 'vinyle' | 'accessoire' | 'autre';
export const TYPE_CONSOMMABLE_LABELS: Record<TypeConsommable, string> = {
  encre:        'Encre',
  papier:       'Papier',
  toner:        'Toner',
  tissu:        'Tissu',
  fil_broderie: 'Fil broderie',
  vinyle:       'Vinyle',
  accessoire:   'Accessoire',
  autre:        'Autre',
};

export type UniteConsommable = 'litre' | 'cartouche' | 'rouleau' | 'kg' | 'pcs' | 'm2';
export const UNITE_CONSOMMABLE_LABELS: Record<UniteConsommable, string> = {
  litre:     'L',
  cartouche: 'cart.',
  rouleau:   'rl',
  kg:        'kg',
  pcs:       'pcs',
  m2:        'm²',
};

export interface MachineImpression {
  id:                  string;
  filialeId:           string;
  code:                string;
  nom:                 string;
  typeMachine:         TypeMachine;
  modele:              string | null;
  numeroSerie:         string | null;
  fournisseur:         string | null;
  dateAcquisition:     string | null;
  prixAchatCents:      string | null;
  compteurInitial:     number;
  compteurActuel:      number;
  statut:              StatutMachineImpression;
  garantieExpire:      string | null;
  contratSav:          boolean;
  contactSav:          string | null;
  derniereMaintenance: string | null;
  notes:               string | null;
  createdAt:           string;
  updatedAt:           string;
  _count?:             { ordres: number; consommables: number };
}

export interface LigneCommande {
  designation:       string;
  famille?:          string;
  quantite:          number;
  unite:             string;
  prixUnitaireCents: number;
  remisePct?:        number;
  montantCents:      number;
}

export interface CommandeImpression {
  id:                  string;
  filialeId:           string;
  reference:           string;
  clientId:            string | null;
  dateCommande:        string;
  dateLivraisonPrevue: string | null;
  dateLivraisonReelle: string | null;
  lignes:              LigneCommande[];
  totalHtCents:        string;
  tvaCents:            string;
  totalTtcCents:       string;
  acompteCents:        string;
  statut:              StatutCommandeImpression;
  adresseLivraison:    string | null;
  instructions:        string | null;
  notes:               string | null;
  createdAt:           string;
  updatedAt:           string;
  client?:             { id: string; fullName: string; companyName: string | null } | null;
  ordres?:             Array<{ id: string; reference: string; designation: string; quantite: number; statut: StatutOFImpression; dateCibleFin: string | null }>;
  _count?:             { ordres: number };
}

export interface OrdreImpression {
  id:                    string;
  filialeId:             string;
  reference:             string;
  commandeId:            string | null;
  machineId:             string | null;
  operateurId:           string | null;
  designation:           string;
  quantite:              number;
  dateLancement:         string;
  dateCibleFin:          string | null;
  dateFinReelle:         string | null;
  priorite:              PrioriteOF;
  statut:                StatutOFImpression;
  tempsEstimeMin:        number | null;
  soustraitant:          string | null;
  soustraitantCoutCents: string | null;
  notes:                 string | null;
  createdAt:             string;
  updatedAt:             string;
  machine?:              { id: string; code: string; nom: string; typeMachine?: TypeMachine } | null;
  operateur?:            { id: string; matricule?: string; fullName: string; department?: string } | null;
  commande?:             { id: string; reference: string } | null;
}

export interface ConsommableImpression {
  id:                string;
  filialeId:         string;
  code:              string;
  designation:       string;
  typeConsommable:   TypeConsommable;
  unite:             UniteConsommable;
  stockActuel:       string;
  stockMinimum:      string;
  prixUnitaireCents: string | null;
  fournisseur:       string | null;
  delaiReappro:      number | null;
  machineRefId:      string | null;
  notes:             string | null;
  createdAt:         string;
  updatedAt:         string;
  machineRef?:       { id: string; code: string; nom: string } | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// TO DO LIST personnelle
// ═══════════════════════════════════════════════════════════════════════════

export type TaskPriority = 'basse' | 'moyenne' | 'haute';
export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  basse:   'Basse',
  moyenne: 'Moyenne',
  haute:   'Haute',
};

export type TaskStatus = 'en_attente' | 'en_cours' | 'terminee' | 'ajournee';
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  en_attente: 'En attente',
  en_cours:   'En cours',
  terminee:   'Terminée',
  ajournee:   'Ajournée',
};
export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  en_attente: 'var(--tx-3)',     // gris
  en_cours:   'var(--p)',         // bleu/primaire
  terminee:   'var(--success)',   // vert
  ajournee:   'var(--warning)',   // orange
};

export interface Task {
  id:           string;
  userId:       string;
  assignedById: string | null;
  filialeId:    string;
  title:        string;
  description:  string | null;
  priority:     TaskPriority;
  status:       TaskStatus;
  dueDate:      string | null;
  completed:    boolean;
  completedAt:  string | null;
  createdAt:    string;
  updatedAt:    string;
  assignedBy?:  { id: string; fullName: string; email: string; role: string } | null;
  user?:        { id: string; fullName: string; email: string; role: string };
  filiale?:     { filialeKey: string; name: string };
}

export interface Colleague {
  id:          string;
  fullName:    string;
  email:       string;
  role:        string;
  filialeCode: string;
  tenantKey?:  string;  // pour admin_holding cross-tenant
}

// ═══════════════════════════════════════════════════════════════════════════
// TEXTIPRINT — BOUTIQUE e-shop (Phase 2 Sprint 1)
// Brief SIM-PLATFORM-BOUTIQUE-2026-001
// ═══════════════════════════════════════════════════════════════════════════

export type UniteVenteBoutique = 'piece' | 'lot' | 'rouleau' | 'kg' | 'litre' | 'carton' | 'metre';
export const UNITE_VENTE_BOUTIQUE_LABELS: Record<UniteVenteBoutique, string> = {
  piece:   'Pièce',
  lot:     'Lot',
  rouleau: 'Rouleau',
  kg:      'Kg',
  litre:   'Litre',
  carton:  'Carton',
  metre:   'Mètre',
};

export type TypeMouvementBoutique =
  | 'entree_fournisseur'
  | 'sortie_vente'
  | 'transfert_production'
  | 'ajustement_inventaire'
  | 'retour_client'
  | 'perte';
export const TYPE_MOUVEMENT_BOUTIQUE_LABELS: Record<TypeMouvementBoutique, string> = {
  entree_fournisseur:    'Entrée fournisseur',
  sortie_vente:          'Sortie vente',
  transfert_production:  'Transfert production',
  ajustement_inventaire: 'Ajustement inventaire',
  retour_client:         'Retour client',
  perte:                 'Perte',
};

export type TypeClientBoutique = 'b2c_particulier' | 'b2b_pro' | 'imprimeur_partenaire';
export const TYPE_CLIENT_BOUTIQUE_LABELS: Record<TypeClientBoutique, string> = {
  b2c_particulier:      'B2C Particulier',
  b2b_pro:              'B2B Pro',
  imprimeur_partenaire: 'Imprimeur partenaire',
};

export type MethodePaiementBoutique = 'wave' | 'orange_money' | 'mtn' | 'virement' | 'especes' | 'cinetpay';
export const METHODE_PAIEMENT_BOUTIQUE_LABELS: Record<MethodePaiementBoutique, string> = {
  wave:         'Wave',
  orange_money: 'Orange Money',
  mtn:          'MTN MoMo',
  virement:     'Virement',
  especes:      'Espèces',
  cinetpay:     'CinetPay',
};

export type TypeAdresseBoutique = 'livraison' | 'facturation';
export const TYPE_ADRESSE_BOUTIQUE_LABELS: Record<TypeAdresseBoutique, string> = {
  livraison:   'Livraison',
  facturation: 'Facturation',
};

export interface FamilleBoutique {
  id:        string;
  filialeId: string;
  code:      string;
  nom:       string;
  ordre:     number;
  iconUrl:   string | null;
  actif:     boolean;
  createdAt: string;
  updatedAt: string;
  _count?:   { sousCategories?: number; produits?: number };
  sousCategories?: SousCategorieBoutique[];
}

export interface SousCategorieBoutique {
  id:        string;
  filialeId: string;
  familleId: string;
  code:      string;
  nom:       string;
  ordre:     number;
  actif:     boolean;
  createdAt: string;
  updatedAt: string;
  famille?:  { id: string; code: string; nom: string } | null;
  _count?:   { produits?: number };
}

export interface ProduitBoutique {
  id:                    string;
  filialeId:             string;
  sku:                   string;
  familleId:             string;
  sousCategorieId:       string | null;
  nom:                   string;
  description:           string | null;
  marque:                string | null;
  refConstructeur:       string | null;
  compatibilites:        string[];
  photos:                string[];
  prixUnitaireHtCents:   string;          // BigInt côté DB sérialisé en string
  tvaPct:                string;
  stockMinimum:          number;
  unite:                 UniteVenteBoutique;
  poidsKg:               string | null;
  personnalisable:       boolean;
  optionsPerso:          Record<string, unknown> | null;
  quantiteMinimum:       number | null;
  delaiProductionJours:  number | null;
  prixSetupCents:        string | null;
  actif:                 boolean;
  featured:              boolean;
  createdAt:             string;
  updatedAt:             string;
  famille?:              { id: string; code: string; nom: string } | null;
  sousCategorie?:        { id: string; code: string; nom: string } | null;
  variantes?:            VarianteBoutique[];
  stocks?:               StockBoutique[];
  _count?:               { variantes?: number; stocks?: number };
}

export interface VarianteBoutique {
  id:           string;
  filialeId:    string;
  produitId:    string;
  skuVariante:  string;
  taille:       string | null;
  couleur:      string | null;
  prixVarCents: string | null;
  actif:        boolean;
  createdAt:    string;
  updatedAt:    string;
  produit?:     { id: string; sku: string; nom: string } | null;
}

export interface StockBoutique {
  id:               string;
  filialeId:        string;
  produitId:        string;
  varianteId:       string | null;
  quantiteActuelle: number;
  quantiteReservee: number;
  derniereEntreeAt: string | null;
  derniereSortieAt: string | null;
  createdAt:        string;
  updatedAt:        string;
  produit?:         { id: string; sku: string; nom: string; unite?: UniteVenteBoutique } | null;
  variante?:        { id: string; skuVariante: string; taille: string | null; couleur: string | null } | null;
}

export interface MouvementStockBoutique {
  id:         string;
  filialeId:  string;
  produitId:  string;
  varianteId: string | null;
  type:       TypeMouvementBoutique;
  quantite:   number;
  motif:      string | null;
  reference:  string | null;
  userId:     string | null;
  createdAt:  string;
  produit?:   { id: string; sku: string; nom: string } | null;
  variante?:  { id: string; skuVariante: string } | null;
  user?:      { id: string; fullName: string; email?: string } | null;
}

export interface RemiseVolumeBoutique {
  id:          string;
  filialeId:   string;
  produitId:   string;
  quantiteMin: number;
  remisePct:   string;
  createdAt:   string;
  produit?:    { id: string; sku: string; nom: string } | null;
}

export interface ClientBoutique {
  id:                  string;
  filialeId:           string;
  contactId:           string | null;
  type:                TypeClientBoutique;
  email:               string;
  phone:               string | null;
  nomComplet:          string;
  raisonSociale:       string | null;
  remisePermanentePct: string;
  totalDepenseCents:   string;
  methodePaiementPref: MethodePaiementBoutique | null;
  actif:               boolean;
  createdAt:           string;
  updatedAt:           string;
  contact?:            { id: string; fullName?: string; companyName?: string | null } | null;
  adresses?:           AdresseClientBoutique[];
  fichiers?:           FichierClientBoutique[];
  _count?:             { adresses?: number; fichiers?: number };
}

export interface AdresseClientBoutique {
  id:        string;
  filialeId: string;
  clientId:  string;
  type:      TypeAdresseBoutique;
  libelle:   string | null;
  ligne1:    string;
  ligne2:    string | null;
  ville:     string;
  zone:      string | null;
  pays:      string;
  estDefaut: boolean;
  createdAt: string;
  client?:   { id: string; nomComplet: string } | null;
}

export interface FichierClientBoutique {
  id:            string;
  filialeId:     string;
  clientId:      string;
  nom:           string;
  url:           string;
  formatFichier: string | null;
  tailleBytes:   number | null;
  createdAt:     string;
  client?:       { id: string; nomComplet: string } | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// TEXTPRINT Sprint 2 — Devis + Sous-traitants
// Brief SIM-PLATFORM-TEXTIPRINT-2026-001 §C (devis) + §H (sous-traitants)
// ═══════════════════════════════════════════════════════════════════════════

export type StatutDevisImpression = 'brouillon' | 'envoye' | 'accepte' | 'refuse' | 'expire' | 'converti';
export const STATUT_DEVIS_LABELS: Record<StatutDevisImpression, string> = {
  brouillon: 'Brouillon',
  envoye:    'Envoyé',
  accepte:   'Accepté',
  refuse:    'Refusé',
  expire:    'Expiré',
  converti:  'Converti en commande',
};

export type StatutSousTraitant = 'actif' | 'inactif' | 'blackliste';
export const STATUT_SOUS_TRAITANT_LABELS: Record<StatutSousTraitant, string> = {
  actif:      'Actif',
  inactif:    'Inactif',
  blackliste: 'Blacklisté',
};

export type SpecialiteSousTraitant =
  | 'grand_format'
  | 'serigraphie'
  | 'broderie'
  | 'offset'
  | 'numerique'
  | 'finition'
  | 'gadgets'
  | 'autre';
export const SPECIALITE_SOUS_TRAITANT_LABELS: Record<SpecialiteSousTraitant, string> = {
  grand_format: 'Grand format',
  serigraphie:  'Sérigraphie',
  broderie:     'Broderie',
  offset:       'Offset',
  numerique:    'Numérique',
  finition:     'Finition',
  gadgets:      'Gadgets',
  autre:        'Autre',
};

export interface LigneDevisImpression {
  id:                string;
  filialeId:         string;
  devisId:           string;
  ordre:             number;
  designation:       string;
  famille:           string | null;
  quantite:          number;
  unite:             string;
  prixUnitaireCents: string;   // BigInt sérialisé
  remisePct:         string;   // Decimal sérialisé
  montantHtCents:    string;   // BigInt sérialisé
  notes:             string | null;
  createdAt:         string;
}

export interface DevisImpression {
  id:                      string;
  filialeId:               string;
  reference:               string;
  clientId:                string | null;
  dateDevis:               string;
  validiteJours:           number;
  dateExpiration:          string;
  totalHtCents:            string;
  remisePct:               string;
  tvaCents:                string;
  totalTtcCents:           string;
  acompteDemandeCents:     string | null;
  acomptePct:              string | null;
  statut:                  StatutDevisImpression;
  delaiLivraisonJours:     number | null;
  conditionsParticulieres: string | null;
  notes:                   string | null;
  commandeIdConvertie:     string | null;
  dateConversion:          string | null;
  dateEnvoi:               string | null;
  dateReponse:             string | null;
  createdAt:               string;
  updatedAt:               string;
  client?:                 { id: string; fullName: string; companyName: string | null } | null;
  lignes?:                 LigneDevisImpression[];
  commandeConvertie?:      { id: string; reference: string; statut: StatutCommandeImpression } | null;
  _count?:                 { lignes?: number; factures?: number };
}

export interface SousTraitant {
  id:              string;
  filialeId:       string;
  raisonSociale:   string;
  specialite:      SpecialiteSousTraitant | null;
  specialiteAutre: string | null;
  contact:         string | null;
  phone:           string | null;
  email:           string | null;
  adresse:         string | null;
  delaiMoyenJours: number | null;
  qualiteNote:     string | null;  // Decimal sérialisé
  tarifsHabituels: Record<string, unknown> | null;
  notes:           string | null;
  statut:          StatutSousTraitant;
  createdAt:       string;
  updatedAt:       string;
  ordres?:         Array<{
    id:           string;
    reference:    string;
    designation:  string;
    quantite:     number;
    statut:       StatutOFImpression;
    dateLancement: string;
    dateCibleFin: string | null;
  }>;
  _count?:         { ordres?: number };
}

// ═══════════════════════════════════════════════════════════════════════════
// TEXTPRINT Sprint 3 — Commandes Boutique (back-office admin)
// Brief SIM-PLATFORM-BOUTIQUE-2026-001 §B2 + brief extension §3 (5 canaux)
// ═══════════════════════════════════════════════════════════════════════════

export type CanalCommandeBoutique = 'web' | 'app' | 'borne' | 'whatsapp' | 'pos';
export const CANAL_COMMANDE_BOUTIQUE_LABELS: Record<CanalCommandeBoutique, string> = {
  web:      'Vitrine web',
  app:      'App mobile',
  borne:    'Borne showroom',
  whatsapp: 'WhatsApp',
  pos:      'Caisse POS',
};

export type StatutCommandeBoutique =
  | 'recue'
  | 'paiement_verifie'
  | 'en_preparation'
  | 'en_production'
  | 'bat_a_valider'
  | 'bat_valide'
  | 'expediee'
  | 'livree'
  | 'terminee'
  | 'annulee';
export const STATUT_COMMANDE_BOUTIQUE_LABELS: Record<StatutCommandeBoutique, string> = {
  recue:            'Reçue',
  paiement_verifie: 'Paiement vérifié',
  en_preparation:   'En préparation',
  en_production:    'En production',
  bat_a_valider:    'BAT à valider',
  bat_valide:       'BAT validé',
  expediee:         'Expédiée',
  livree:           'Livrée',
  terminee:         'Terminée',
  annulee:          'Annulée',
};

export type ModeLivraisonBoutique =
  | 'livraison_abidjan'
  | 'livraison_partenaire'
  | 'retrait_boutique'
  | 'livraison_interieur';
export const MODE_LIVRAISON_BOUTIQUE_LABELS: Record<ModeLivraisonBoutique, string> = {
  livraison_abidjan:    'Livraison Abidjan (interne)',
  livraison_partenaire: 'Livraison partenaire',
  retrait_boutique:     'Retrait boutique (Angré)',
  livraison_interieur:  'Livraison intérieur CI',
};

export type StatutPaiementBoutique = 'en_attente' | 'valide' | 'echoue' | 'rembourse';
export const STATUT_PAIEMENT_BOUTIQUE_LABELS: Record<StatutPaiementBoutique, string> = {
  en_attente: 'En attente',
  valide:     'Validé',
  echoue:     'Échoué',
  rembourse:  'Remboursé',
};

export interface LigneCommandeBoutique {
  id:                string;
  filialeId:         string;
  commandeId:        string;
  produitId:         string;
  varianteId:        string | null;
  designation:       string;
  sku:               string;
  quantite:          number;
  prixUnitaireCents: string;   // BigInt sérialisé
  remisePct:         string;   // Decimal sérialisé
  montantHtCents:    string;   // BigInt sérialisé
  personnalisation:  Record<string, unknown> | null;
  batUrl:            string | null;
  batValide:         boolean;
  notes:             string | null;
  createdAt:         string;
  produit?:          { id: string; sku: string; nom: string } | null;
  variante?:         { id: string; skuVariante: string; taille: string | null; couleur: string | null } | null;
}

export interface PaiementBoutique {
  id:             string;
  filialeId:      string;
  commandeId:     string;
  methode:        MethodePaiementBoutique;
  statut:         StatutPaiementBoutique;
  montantCents:   string;   // BigInt sérialisé
  reference:      string | null;
  cinetpayId:     string | null;
  recuParUserId:  string | null;
  dateValidation: string | null;
  notes:          string | null;
  createdAt:      string;
  updatedAt:      string;
  recuPar?:       { id: string; fullName: string; email: string } | null;
}

export interface LivraisonBoutique {
  id:              string;
  filialeId:       string;
  commandeId:      string;
  mode:            ModeLivraisonBoutique;
  adresseLigne1:   string;
  adresseLigne2:   string | null;
  ville:           string;
  zone:            string | null;
  pays:            string;
  livreurNom:      string | null;
  livreurPhone:    string | null;
  trackingCode:    string | null;
  dateExpedition:  string | null;
  dateLivraison:   string | null;
  signatureClient: string | null;
  notes:           string | null;
  createdAt:       string;
  updatedAt:       string;
}

export interface CommandeBoutique {
  id:                    string;
  filialeId:             string;
  reference:             string;
  canal:                 CanalCommandeBoutique;
  clientId:              string | null;
  emailCommande:         string;
  phoneCommande:         string | null;
  nomCompletCommande:    string;
  raisonSocialeCommande: string | null;
  sousTotalHtCents:      string;   // BigInt sérialisé
  remiseClientCents:     string;
  remiseCodeCents:       string;
  fraisLivraisonCents:   string;
  tvaCents:              string;
  totalCents:            string;
  statut:                StatutCommandeBoutique;
  ordreImpressionId:     string | null;
  notesClient:           string | null;
  notesInternes:         string | null;
  dateCommande:          string;
  dateLivraisonPrevue:   string | null;
  dateLivraisonReelle:   string | null;
  trackingToken:         string | null;
  createdAt:             string;
  updatedAt:             string;
  client?:               {
    id: string;
    nomComplet: string;
    email: string;
    phone?: string | null;
    raisonSociale?: string | null;
    type?: TypeClientBoutique;
  } | null;
  lignes?:               LigneCommandeBoutique[];
  paiements?:            PaiementBoutique[];
  livraison?:            LivraisonBoutique | null;
  _count?:               { lignes?: number; paiements?: number };
}
